/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-incorrect-calls-with-inline-anonymous-functions */
/* eslint-disable ember/no-runloop */
import { action, computed } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import HotelStrategyStrategyDetailBaseController from 'lumen/controllers/hotel/strategy/strategy-detail-base';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  SUBTYPE_VALUES_KEY_STRATEGY,
  TYPE_ID_CANCELLATION,
} from 'lumen/utils/constants/rate-strategy-constants';

const TAB_HEADER_HEIGHT = 56;

export default class HotelStrategyStrategyDetailWithTabsBaseController extends HotelStrategyStrategyDetailBaseController {
  queryParams = [
    'sortBy',
    'sortDirection',
    'tab',
    'offset',
    'subtype',
    'entity',
  ];

  @service rsSelections;

  @tracked offset = 0;
  @tracked subtype = this.subtypes[0]?.name;
  @tracked tab = DEFAULT_QP_VALUES.hotel.strategy.tab;

  /**
   * Returns what data needs to be shown in the evolution chart (strategies or cancellation policies)
   * Can be overwritten in the route if needed
   */
  get entityToDisplay() {
    // For Non-refundable show strategies
    return SUBTYPE_VALUES_KEY_STRATEGY;
  }

  get formattedDates() {
    if (!this.historicData) {
      return [];
    }

    const formattedDates = this.historicData?.map((week) =>
      dayjs(week.extractDate).format('L'),
    );

    return formattedDates;
  }

  get formattedHistoricData() {
    if (!this.historicData) {
      return [];
    }

    // Cancellation policies are bundles under the dummy 'cancellation' type and need to be accessed by subtype
    const type =
      this.typeId === TYPE_ID_CANCELLATION ? this.subtypeId : this.typeId;
    // Cancellation policy don't have a fixed entity to display, check entityToDisplay for which one to display
    const entity = this.entityToDisplay;

    const mappedSubtypes = this.historicData
      .map((ratestrategy) =>
        ratestrategy.rstypes
          .find((rstype) => rstype.name === type)
          .rssubtypes.find((rssubtype) => rssubtype.name === this.subtypeId),
      )
      .filter((subtype) => subtype); // In case the API does not return strategies we need to filter out the empty ones

    const weeks =
      entity === SUBTYPE_VALUES_KEY_STRATEGY
        ? mappedSubtypes.map((subtype) => subtype.rsstrategies)
        : mappedSubtypes.map((subtype) => subtype.rscancellationpolicies);

    return weeks.map((week) =>
      week.filter((strategy) => {
        if (strategy.isOwnHotel) {
          return strategy;
        }
        return this.rsSelections.filteredCompetitors.includes(
          strategy.hotelinfo,
        );
      }),
    );
  }

  @computed('model.historicData.value')
  get historicData() {
    return this.model?.historicData?.value;
  }

  @computed('model.historicData.isRunning')
  get isChartLoading() {
    return this.model?.historicData?.isRunning;
  }

  get subtypeId() {
    // Selected subtype in the dropdown or first subtype by default
    return this.subtype ?? this.subtypes[0].name;
  }

  @computed('model.type')
  get type() {
    return this.model?.type;
  }

  @action
  chooseCountry(country) {
    this.country = country.id;
    this.subtype = `pos_${this.ota}_${country.id}`;
  }

  @action
  chooseEntity(entity) {
    this.entity = entity.id;
  }

  @action
  chooseOta(ota) {
    this.ota = ota.id;
    if (this.subtypes[0]?.isMobileType) {
      this.subtype = `mobile_${ota.id}_${this.platform}`;
    }

    if (this.subtypes[0]?.isPosType) {
      this.subtype = `pos_${ota.id}_${this.country}`;
    }
  }

  @action
  choosePlatform(platform) {
    this.platform = platform.id;
    this.subtype = `mobile_${this.ota}_${platform.id}`;
  }

  @action
  chooseSubtype(subtype) {
    this.subtype = subtype.id;
  }

  @action
  makeActiveTab(tabId) {
    this.tab = tabId;
  }

  @action
  resetContainerDimensions() {
    scheduleOnce('afterRender', this, () => {
      this.height =
        document.querySelector('.modal--stretched')?.offsetHeight -
        TAB_HEADER_HEIGHT;
      this.width = document.querySelector('.modal--stretched')?.offsetWidth;
    });
  }

  @action
  updateOffset(offset) {
    this.offset = offset;
  }
}
